
/**
 * Menu/Feature Access Permissions
 * These define what features and menu items a user can access
 * Admin role automatically has access to ALL permissions
 */
export const AVAILABLE_PERMISSIONS = [
  // Dashboard & Basics
  "dashboard_view", // View dashboard/calendar
  "settings_view", // View and manage personal settings

  // User Management
  "user_management", // Access to Users menu - view, create, update, delete users and manage roles
  "edit_user_status", // Permission to change user status (active, inactive, suspended)
  "change_user_role", // Permission to change user roles (with role hierarchy restrictions)

  // Analytics & Reports
  "analytics_view", // View analytics dashboard
  "reports_management", // Access to Reports menu - view and manage business reports

  // Content Management
  "updates_management", // Access to Updates menu - create, edit, publish updates
  "careers_management", // Access to Careers menu - manage job positions and applications
  "sections_management", // Access to Sections menu - manage page sections and content
  "community_management", // Access to Community menu - manage community features
  "documents_management", // Access to Documents menu - manage documents and files
  "hero_backgrounds_management", // Access to Hero Backgrounds menu - manage hero section images
  "contact_info_management", // Access to Contact Info menu - manage contact information

  // Support & Information
  "customer_support", // Access to support/ticket system

  // System & Configuration
  "system_admin", // System-level administrative access
  "social_links", // Access to Social Links menu - manage social media links
  "view_audit_trail", // Access to view audit logs and system activity
] as const;

export type Permission = (typeof AVAILABLE_PERMISSIONS)[number];

/**
 * Permission to Menu Item Mapping
 * Associates each permission with its corresponding menu item
 * Used for navigation filtering and access control
 */
export const PERMISSION_MENU_MAP: Record<Permission, null | string> = {
  analytics_view: null, // Dashboard feature, not a menu item
  careers_management: "Careers",
  change_user_role: null, // Feature, not a menu item
  community_management: "Community",
  contact_info_management: "Contact Info",
  customer_support: null, // Support feature
  dashboard_view: "Calendar",
  documents_management: "Documents",
  edit_user_status: null, // Feature, not a menu item
  hero_backgrounds_management: "Hero Backgrounds",
  reports_management: "Reports",
  sections_management: "Sections",
  settings_view: "Settings",
  social_links: "Social Links",
  system_admin: null, // System-level feature
  updates_management: "Updates",
  user_management: "Users",
  view_audit_trail: null, // System feature
} as const;

/**
 * Role Hierarchy for Permission Changes
 * Determines which roles can assign which roles to other users
 * 
 * CRITICAL RULE: Only ADMIN can assign/change the ADMIN role
 * Non-admin users (Manager, Staff) can only assign roles below their level
 * 
 * - admin: Can assign ALL roles (admin, manager, staff)
 * - manager: Can assign ONLY staff roles (NOT admin or other managers)
 * - staff: Cannot assign ANY roles
 */
export const ROLE_HIERARCHY = {
  admin: ["admin", "manager", "staff"], // Admin can assign all roles
  manager: ["staff"], // Manager can only assign Staff roles
  staff: [], // Staff cannot assign any roles
} as const;

/**
 * Default Permissions by Role
 * Defines what permissions each role has by default
 * Admin always has all permissions regardless of this setting
 */
export const DEFAULT_ROLE_PERMISSIONS: Record<string, Permission[]> = {
  admin: [], // Admin gets all permissions automatically
  manager: [
    "dashboard_view",
    "settings_view",
    "user_management",
    "reports_management",
    "documents_management",
  ],
  staff: [
    "dashboard_view",
    "settings_view",
    "reports_management",
    "documents_management",
  ],
};

/**
 * Permissions that are always accessible to all users (including non-admin)
 * These represent basic features everyone should have access to
 */
export const ALWAYS_ACCESSIBLE_PERMISSIONS: readonly Permission[] = [
  "dashboard_view", // Calendar is always visible
  "settings_view", // Settings is always visible
] as const;

/**
 * Check if a user can access a specific feature/permission
 * @param userRole - The user's role
 * @param userPermissions - Array of permission strings the user has
 * @param requiredPermission - The permission to check
 * @returns true if user can access the feature, false otherwise
 */
export function canAccessPermission(
  userRole: string | undefined,
  userPermissions: Permission[] | undefined,
  requiredPermission: Permission | undefined,
): boolean {
  // Admin users have access to everything
  if (userRole === "admin") {
    return true;
  }

  // If no permission is required, deny access (must be explicitly allowed)
  if (!requiredPermission) {
    return false;
  }

  // Always allow access to basic features (Calendar, Settings)
  if (ALWAYS_ACCESSIBLE_PERMISSIONS.includes(requiredPermission)) {
    return true;
  }

  // Check if user has the required permission
  return userPermissions?.includes(requiredPermission) ?? false;
}

/**
 * Check if a user can change another user's role
 * Considers both the permission and role hierarchy
 * RULE: Only ADMIN can assign/change the admin role
 * @param changerRole - The role of user making the change
 * @param targetRole - The role to be assigned
 * @returns true if change is allowed, false otherwise
 */
export function canChangeUserRole(
  changerRole: string | undefined,
  targetRole: string | undefined,
): boolean {
  if (!changerRole || !targetRole) {
    return false;
  }

  // Only ADMIN can assign the admin role to someone
  if (targetRole === "admin" && changerRole !== "admin") {
    return false;
  }

  // Admin can change any role
  if (changerRole === "admin") {
    return true;
  }

  // Get the roles this user can assign (based on hierarchy)
  const assignableRoles = ROLE_HIERARCHY[changerRole as keyof typeof ROLE_HIERARCHY] || [];
  return assignableRoles.includes(targetRole as never);
}

/**
 * Get all available permissions a specific role can potentially have
 * This helps in permission assignment UI
 * @param userRole - The user's role performing the assignment
 * @returns Array of permissions that can be assigned
 */
export function getAssignablePermissions(userRole: string | undefined): Permission[] {
  switch (userRole) {
    case "admin":
      return AVAILABLE_PERMISSIONS as unknown as Permission[];
    case "manager":
      return [
        "dashboard_view",
        "settings_view",
        "user_management",
        "reports_management",
        "documents_management",
      ] as Permission[];
    case "staff":
      return [
        "dashboard_view",
        "settings_view",
        "reports_management",
      ] as Permission[];
    default:
      return [];
  }
}

/**
 * Get the list of roles a user can assign
 * @param userRole - The user's role
 * @returns Array of role names that can be assigned
 */
export function getAvailableRolesToAssign(userRole: string | undefined): readonly string[] {
  if (!userRole) {
    return [];
  }
  return ROLE_HIERARCHY[userRole as keyof typeof ROLE_HIERARCHY] || [];
}

/**
 * Get the reason why a user cannot change another user's role
 * Useful for displaying error messages in the UI
 * @param changerRole - The role of user making the change
 * @param targetRole - The role to be assigned
 * @returns String explaining why change is denied, or null if change is allowed
 */
export function getRoleChangeBlockReason(
  changerRole: string | undefined,
  targetRole: string | undefined,
): null | string {
  if (!changerRole || !targetRole) {
    return "Invalid roles provided";
  }

  // Only ADMIN can assign the admin role
  if (targetRole === "admin" && changerRole !== "admin") {
    return "Only Administrators can assign the Admin role";
  }

  // Admin can change any role (no restriction)
  if (changerRole === "admin") {
    return null;
  }

  // Check role hierarchy
  const assignableRoles = ROLE_HIERARCHY[changerRole as keyof typeof ROLE_HIERARCHY] || [];
  if (!assignableRoles.includes(targetRole as never)) {
    const availableRoles = assignableRoles.join(", ") || "no roles";
    return `Your role can only assign: ${availableRoles}`;
  }

  return null;
}
